// ===========================================
// INPUT COMPONENT - Premium UI
// ===========================================

import React, { forwardRef } from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  variant?: 'default' | 'filled' | 'glass';
  fullWidth?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  icon,
  iconPosition = 'left',
  variant = 'default',
  fullWidth = false,
  className = '',
  id,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  const baseStyles = `
    w-full px-4 py-3 rounded-xl font-medium
    transition-all duration-300 ease-out
    focus:outline-none focus:ring-2 focus:ring-offset-0
    placeholder:text-gray-400
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  const variants = {
    default: `
      bg-white border-2 border-gray-200
      text-gray-900
      hover:border-tripnovac-300
      focus:border-tripnovac-500 focus:ring-tripnovac-500/30
      ${error ? 'border-red-400 focus:border-red-500 focus:ring-red-500/30' : ''}
    `,
    filled: `
      bg-gray-100 border-2 border-transparent
      text-gray-900
      hover:bg-gray-150
      focus:bg-white focus:border-tripnovac-500 focus:ring-tripnovac-500/30
      ${error ? 'border-red-400 focus:border-red-500 focus:ring-red-500/30' : ''}
    `,
    glass: `
      glass-card border-2 border-white/20
      text-gray-900
      hover:border-white/30
      focus:border-tripnovac-500/50 focus:ring-tripnovac-500/30
      ${error ? 'border-red-400/50 focus:border-red-500 focus:ring-red-500/30' : ''}
    `,
  };

  return (
    <div className={`${fullWidth ? 'w-full' : ''} ${className}`}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-semibold text-gray-700 mb-2"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {icon && iconPosition === 'left' && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
            {icon}
          </div>
        )}
        <input
          ref={ref}
          id={inputId}
          className={`
            ${baseStyles}
            ${variants[variant]}
            ${icon && iconPosition === 'left' ? 'pl-12' : ''}
            ${icon && iconPosition === 'right' ? 'pr-12' : ''}
          `}
          {...props}
        />
        {icon && iconPosition === 'right' && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
            {icon}
          </div>
        )}
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-500 font-medium">{error}</p>
      )}
      {helperText && !error && (
        <p className="mt-2 text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
